import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {Button, Divider, Spin, Flex, Typography} from 'antd';
import {DownloadOutlined, FolderOpenTwoTone} from '@ant-design/icons';
import {ModelDTO} from 'hemwb-api';
import {useModelFileTreeLoader, TreeNode, modelFileItemDownload} from '../../../../store/modelFile';
import {modelDownload} from '../../../../store/model';
import {downloadFromResponse} from '../../../../utils/download';
import {ModelFileDownloadModal} from './ModelFileDownloadModal';
import {FileTreeNode} from './FileTreeNode';
import styles from './ModelFilesTab.module.scss';
//import {canDownloadModelFiles} from '../../../../permissions/usePermissions';
//import {PERMISSION} from '../../../../permissions/types';

interface SupportingDocumentsTabProps {
  model: ModelDTO | null;
}

/**
 * Supporting Documents tab - displays supporting documents in a folder/table structure
 * Uses the /treeaudited endpoint without filters to show all supporting documents
 */
export const SupportingDocumentsTab: React.FC<SupportingDocumentsTabProps> = ({model}) => {
  const dispatch = useDispatch();
  // Use /treeaudited endpoint without filters for supporting documents
  const {data: files, load: loadFiles} = useModelFileTreeLoader(model?.uuid || '');
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadType, setDownloadType] = useState<'all' | 'single'>('all');
  const [selectedFile, setSelectedFile] = useState<{uuid: string; name: string} | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (model?.uuid) {
      loadFiles();
    }
  }, [model?.uuid]);

  // Check if user has permission to download files
  //const canDownload = canDownloadModelFiles(model as ModelDetailDTO);

  const handleDownloadAll = () => {
    setDownloadType('all');
    setSelectedFile(null);
    setDownloadModalVisible(true);
  };

  const handleDownloadFile = (fileUuid: string, fileName: string) => {
    setDownloadType('single');
    setSelectedFile({uuid: fileUuid, name: fileName});
    setDownloadModalVisible(true);
  };

  const handleCloseModal = () => {
    setDownloadModalVisible(false);
    setSelectedFile(null);
  };

  const handleConfirmDownload = () => {
    handleCloseModal();
    setIsDownloading(true);

    if (downloadType === 'all' && model?.uuid) {
      // Download all files in the model
      modelDownload(dispatch, model.uuid)
        .then((response) => downloadFromResponse(response.raw))
        .catch(() => null)
        .finally(() => setIsDownloading(false));
    } else if (selectedFile && model?.uuid) {
      // Download individual file
      modelFileItemDownload(dispatch, model.uuid, selectedFile.uuid)
        .then((response) => downloadFromResponse(response.raw))
        .catch(() => null)
        .finally(() => setIsDownloading(false));
    }
  };

  if (!model) {
    return <div>No model selected</div>;
  }

  if (!files) {
    return (
      <div style={{textAlign: 'center', padding: '40px'}}>
        <Spin size="large" />
      </div>
    );
  }

  // Filter to get only root-level items (path equals name, no parent folders)
  const rootItems = files.filter((f: any) => {
    const path = f.path || '';
    return path === f.name || path === `/${f.name}`;
  });

  const hasFiles = rootItems.length > 0;

  return (
    <div className={styles.modelFilesTab}>
      {/* Header with description and Download All button */}
      <div className={styles.header}>
        <span className={styles.description}>
          Browse and download supporting documents and additional model resources
        </span>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadAll}
          loading={isDownloading}
          disabled={isDownloading || !hasFiles}
        >
          Download All
        </Button>
      </div>

      <Divider />

      {!hasFiles ? (
        <Flex vertical gap="10px" style={{alignItems: 'center', padding: '0 16px 16px'}}>
          <FolderOpenTwoTone twoToneColor={[styles.primaryTeal, styles.lockColor]} style={{fontSize: '39px'}} />
          <Typography.Text strong style={{fontSize: '16px'}}>
            No files available
          </Typography.Text>
          <Typography.Text type="secondary" style={{textAlign: 'center', maxWidth: '400px'}}>
            There are currently no files uploaded for this entry.
          </Typography.Text>
        </Flex>
      ) : (
        <>
          {/* File tree with folders and file table */}
          <FileTreeNode
            files={rootItems as TreeNode[]}
            isRoot={true}
            defaultFolderOpen={true}
            onDownloadFile={handleDownloadFile}
          />
        </>
      )}

      {/* Download confirmation modal */}
      <ModelFileDownloadModal
        visible={downloadModalVisible}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDownload}
        model={model}
        downloadType={downloadType}
        fileName={selectedFile?.name}
      />
    </div>
  );
};
